import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 ease-out transform-gpu hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none motion-reduce:transform-none motion-reduce:transition-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-300 hover:[&_svg]:scale-110",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-light shadow-soft",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-soft",
        outline: "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground transition-gentle",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-soft",
        ghost: "text-foreground hover:bg-muted hover:text-foreground transition-gentle",
        link: "text-primary underline-offset-4 hover:underline",
        therapy: "bg-primary text-primary-foreground hover:bg-primary-light shadow-therapy transition-therapy",
        calm: "bg-muted text-foreground hover:bg-muted/80 transition-gentle border border-border",
        voice: "bg-primary/20 text-primary hover:bg-primary/30 border-2 border-primary hover:border-primary-light transition-therapy",
        // Teal gradient button - premium CTA style
        gradient: "bg-gradient-to-r from-[hsl(177,41%,50%)] to-[hsl(182,37%,46%)] hover:from-[hsl(177,41%,55%)] hover:to-[hsl(182,37%,50%)] text-white font-light shadow-lg shadow-[hsl(177,41%,50%)]/25 border-0 active:scale-[0.98] transition-all duration-200",
        // Light teal gradient - for dark text CTAs like "Begin My Journey"
        gradientLight: "bg-gradient-to-r from-[hsl(177,41%,76%)] to-[hsl(182,37%,46%)] hover:from-[hsl(177,41%,80%)] hover:to-[hsl(182,37%,50%)] text-[hsl(182,29%,20%)] font-medium shadow-lg shadow-[hsl(177,41%,76%)]/20 border-0 active:scale-[0.98] transition-all duration-200",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        voice: "h-16 w-16 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
